/**
 * @file Catalogue des skins de munitions : 5 par categorie d'arme (comportement), purement cosmetiques (balles, traines, impacts, flash de bouche).
 * Ajouter un skin = une entree de donnees (un theme x un comportement) ; le rendu lit `style` sans connaitre le skin.
 */
import type { WeaponBehavior } from '@shared/enums';
import { WEAPON_BEHAVIORS } from '@shared/enums';
import type { AmmoImpact, AmmoShape, AmmoSkinDef, AmmoStyle, AmmoTrail, LocalizedName, WalletAmounts } from './types';

/** Style d'un theme applique a un comportement : forme, couleurs, traine, impact, flash. */
interface Theme {
  id: string;
  name: LocalizedName;
  /** [couleur principale, couleur secondaire] (halo / coeur / particules). */
  colors: [string, string];
  shape: AmmoShape;
  trail: AmmoTrail;
  impact: AmmoImpact;
  glow: boolean;
  rainbow?: boolean;
  size?: number;
}

/** Themes reutilises par toutes les categories (le rendu adapte la forme : balle, plomb, grenade, missile, faisceau). */
const THEMES: Record<string, Theme> = {
  candy: { id: 'candy', name: { fr: 'Bonbon', en: 'Candy' }, colors: ['#ff8fc7', '#7ff0d0'], shape: 'orb', trail: 'sparkle', impact: 'confetti', glow: false },
  neon: { id: 'neon', name: { fr: 'Neon', en: 'Neon' }, colors: ['#33f5ff', '#ff3df0'], shape: 'capsule', trail: 'sparkle', impact: 'spark', glow: true },
  fire: { id: 'fire', name: { fr: 'Braise', en: 'Ember' }, colors: ['#ff7a2e', '#ffd23c'], shape: 'orb', trail: 'embers', impact: 'flame', glow: true },
  frost: { id: 'frost', name: { fr: 'Givre', en: 'Frost' }, colors: ['#9fe4ff', '#ffffff'], shape: 'diamond', trail: 'snow', impact: 'snow', glow: true },
  love: { id: 'love', name: { fr: 'Amour', en: 'Love' }, colors: ['#ff5f9a', '#ffc2da'], shape: 'heart', trail: 'hearts', impact: 'hearts', glow: false },
  fishy: { id: 'fishy', name: { fr: 'Poisson', en: 'Fishy' }, colors: ['#4fd1c5', '#ffb45e'], shape: 'fish', trail: 'bubbles', impact: 'bubbles', glow: false },
  rainbow: { id: 'rainbow', name: { fr: 'Arc-en-ciel', en: 'Rainbow' }, colors: ['#ff6b6b', '#6b9bff'], shape: 'orb', trail: 'rainbow', impact: 'confetti', glow: true, rainbow: true },
  starry: { id: 'starry', name: { fr: 'Etoile', en: 'Starry' }, colors: ['#ffd84a', '#a78bff'], shape: 'star', trail: 'sparkle', impact: 'stars', glow: true },
  blossom: { id: 'blossom', name: { fr: 'Sakura', en: 'Blossom' }, colors: ['#ffb3d1', '#ffffff'], shape: 'orb', trail: 'petals', impact: 'petals', glow: false },
  gold: { id: 'gold', name: { fr: 'Or', en: 'Gold' }, colors: ['#ffd23c', '#fff3b0'], shape: 'diamond', trail: 'sparkle', impact: 'stars', glow: true },
};

/** Style "classique" (gratuit) de chaque categorie : reprend le rendu d'origine. */
const CLASSIC: Record<WeaponBehavior, AmmoStyle> = {
  single: { shape: 'capsule', colors: ['#ffe066', '#fff6b8'], glow: false, trail: 'none', impact: 'spark', size: 1 },
  pierce: { shape: 'capsule', colors: ['#8ff0ff', '#e3fbff'], glow: false, trail: 'none', impact: 'spark', size: 1 },
  shotgun: { shape: 'orb', colors: ['#ffd24a', '#fff0a8'], glow: false, trail: 'none', impact: 'spark', size: 1 },
  explosive: { shape: 'orb', colors: ['#ff9a3c', '#ffd9a0'], glow: false, trail: 'none', impact: 'flame', size: 1 },
  laser: { shape: 'capsule', colors: ['#7ff3ff', '#ffffff'], glow: true, trail: 'none', impact: 'spark', size: 1 },
  missile: { shape: 'capsule', colors: ['#ff5a5a', '#cfd5de'], glow: false, trail: 'smoke', impact: 'flame', size: 1 },
  blastSniper: { shape: 'capsule', colors: ['#ff7ad9', '#ffffff'], glow: true, trail: 'none', impact: 'flame', size: 1 },
};

/** Les 4 themes proposes en plus du classique pour chaque categorie (choisis pour leur lisibilite avec la forme du projectile). */
const THEMES_OF: Record<WeaponBehavior, readonly string[]> = {
  single: ['candy', 'love', 'fishy', 'starry'],
  pierce: ['neon', 'frost', 'rainbow', 'gold'],
  shotgun: ['candy', 'blossom', 'fire', 'starry'],
  explosive: ['fire', 'candy', 'rainbow', 'blossom'],
  laser: ['neon', 'rainbow', 'frost', 'love'],
  missile: ['fishy', 'fire', 'love', 'starry'],
  blastSniper: ['neon', 'gold', 'rainbow', 'frost'],
};

const CLASSIC_NAME: LocalizedName = { fr: 'Classique', en: 'Classic' };

/** Cout : 1er theme 150 gemmes, puis +150 ; les themes les plus spectaculaires (arc-en-ciel, or) se paient en Rubis. */
const costOf = (themeId: string, rank: number): WalletAmounts => (themeId === 'rainbow' || themeId === 'gold' ? { ruby: 15 + 5 * rank } : { dia: 150 * (rank + 1) });

export const AMMO_SKINS: readonly AmmoSkinDef[] = WEAPON_BEHAVIORS.flatMap((behavior): AmmoSkinDef[] => [
  { id: `${behavior}_classic`, behavior, name: CLASSIC_NAME, cost: {}, style: CLASSIC[behavior] },
  ...THEMES_OF[behavior].map((tid, rank): AmmoSkinDef => {
    const t = THEMES[tid] as Theme;
    return {
      id: `${behavior}_${t.id}`,
      behavior,
      name: t.name,
      cost: costOf(t.id, rank),
      style: { shape: t.shape, colors: t.colors, glow: t.glow, trail: t.trail, impact: t.impact, rainbow: t.rainbow, size: t.size ?? 1 },
    };
  }),
]);

/** Skin equipe par defaut (et possede d'emblee) de chaque categorie. */
export const DEFAULT_AMMO_SKIN: Readonly<Record<WeaponBehavior, string>> = Object.fromEntries(WEAPON_BEHAVIORS.map((b) => [b, `${b}_classic`])) as Record<WeaponBehavior, string>;
